import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="bg-navy h-2" />
      <div className="bg-crimson h-1" />
      <div className="flex-1 px-4 py-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="section-eyebrow">ASE</div>
            <h1 className="text-3xl font-bold mt-2">Privacy Policy</h1>
            <div className="rule-crimson" />
            <p className="text-muted text-sm mt-2">Last updated: May 1, 2026</p>
          </div>

          <div className="card p-6 space-y-6 text-sm leading-relaxed text-navy">
            <section>
              <h2 className="text-lg font-semibold mb-2">1. About this policy</h2>
              <p>
                ASE CRM (the &quot;Service&quot;) is an internal customer
                relationship management application operated by John Adams
                &amp; Associates LLC (&quot;we&quot;, &quot;us&quot;) on behalf
                of ASE. It is
                used by ASE sales staff (&quot;employee users&quot;) and their
                supervisors. This policy describes what information we collect
                from those employee users, how we use it, and the choices they
                have. The Service is not intended for, and is not offered to,
                the general public.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">2. Information we collect</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <b>Account information:</b> name, work email address,
                  assigned dealership and branch, role, and password hash.
                </li>
                <li>
                  <b>Mobile phone number:</b> only if the employee voluntarily
                  provides it to receive SMS notifications (see section 4).
                </li>
                <li>
                  <b>Work product:</b> customer visit notes (including audio
                  transcribed by a speech-to-text provider), follow-up tasks,
                  pipeline opportunities, weekly recaps, and related
                  metadata.
                </li>
                <li>
                  <b>Device and usage data:</b> IP address, browser/user-agent,
                  push-notification subscription endpoints, sign-in
                  timestamps, and audit-log entries for sensitive actions.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">3. How we use information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To operate the Service and provide it to ASE.</li>
                <li>To deliver in-app, push, email, and SMS notifications you
                    or your employer have configured.</li>
                <li>To maintain security, detect abuse, and produce audit
                    trails required by ASE.</li>
                <li>To generate AI-assisted summaries and weekly recaps from
                    notes you author. Note content is processed by an AI
                    service provider and a transcription provider strictly to return
                    the requested output and is not used by them to train
                    their models.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">4. SMS messaging and mobile opt-in</h2>
              <p>
                Employees may opt in to receive SMS notifications by entering
                their mobile number on the Settings screen and checking the
                consent box. Message types include high-priority customer
                activity alerts, weekly-recap reminders, and escalation
                pings. Message frequency varies based on activity (typically
                fewer than 10 messages per week per user). Message and data
                rates may apply. Reply <b>HELP</b> for help, or <b>STOP</b> to
                opt out at any time. Opting out disables SMS only; you will
                still receive in-app and email notifications.
              </p>
              <p className="mt-2 font-semibold">
                We do not share, sell, rent, or release mobile phone numbers
                or SMS opt-in data to any third party or affiliate for
                marketing or promotional purposes. Mobile information is used
                solely to deliver the Service&apos;s notifications and is
                shared only with the SMS aggregator (Twilio) strictly to
                transmit those messages.
              </p>
              <p className="mt-2">
                Full SMS terms are available at{' '}
                <Link to="/sms-terms" className="text-crimson hover:underline">
                  /sms-terms
                </Link>.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">5. How we share information</h2>
              <p>We share information only as needed to operate the Service:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><b>ASE:</b> your employer can view data within its own
                    dealership and branch scope, consistent with role-based
                    access controls.</li>
                <li><b>Service providers (sub-processors):</b> Twilio (SMS),
                    Mailgun (email), an AI service provider (processing of note text),
                    a speech-to-text provider (audio transcription), and our
                    cloud hosting provider. Each is contractually limited to
                    processing data on our instructions.</li>
                <li><b>Legal:</b> when required by law or to protect rights,
                    safety, or property.</li>
              </ul>
              <p className="mt-2">
                We do <b>not</b> sell personal information, and we do not use
                it for cross-context behavioral advertising.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">6. Data retention</h2>
              <p>
                Account and work-product records are retained for as long as
                the employee&apos;s account is active and afterward as
                required for ASE&apos;s recordkeeping. Mobile numbers are
                deleted from the user&apos;s profile within 30 days of a
                STOP/opt-out (we retain a minimal opt-out record solely to
                honor the request). Audit logs are retained per ASE
                policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">7. Security</h2>
              <p>
                Data is transmitted over TLS, passwords are stored as bcrypt
                hashes, and access is restricted by role-based and
                row-level (dealership/branch) controls. No system is
                perfectly secure, but we work to protect information against
                unauthorized access, disclosure, and loss.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">8. Your choices</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Update your profile or remove your mobile number at any
                    time on the Settings screen.</li>
                <li>Reply <b>STOP</b> to any SMS to opt out of messaging.</li>
                <li>Disable browser push notifications in your browser&apos;s
                    site settings.</li>
                <li>Contact us using the address below to request access,
                    correction, or deletion of your data, subject to
                    ASE&apos;s recordkeeping requirements.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">9. Children</h2>
              <p>
                The Service is for ASE employees and is not directed to
                anyone under 18. We do not knowingly collect information from
                children.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">10. Changes</h2>
              <p>
                We may update this policy from time to time. The &quot;Last
                updated&quot; date above reflects the most recent revision.
                Material changes will be communicated through the Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-2">11. Contact</h2>
              <p>
                John Adams &amp; Associates LLC<br />
                Email:{' '}
                <a href="mailto:john@adamsfirst.com" className="text-crimson hover:underline">
                  john@adamsfirst.com
                </a>
              </p>
            </section>
          </div>

          <div className="mt-6 text-center text-sm">
            <Link to="/sms-terms" className="text-navy hover:underline">SMS Terms</Link>
            <span className="text-muted mx-2">·</span>
            <Link to="/login" className="text-navy hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
